import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreateIcon from '@mui/icons-material/Create';
import ErrorIcon from '@mui/icons-material/Error';
import ImageIcon from '@mui/icons-material/Image';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PendingIcon from '@mui/icons-material/Pending';
import UploadIcon from '@mui/icons-material/Upload';

import {
  Box,
  LinearProgress,
  Typography,
  Paper,
  Stack,
  Chip,
  Fade,
} from '@mui/material';

interface RealProgressProps {
  isVisible: boolean;
  onComplete?: () => void;
  currentStep?: string;
  stepProgress?: number;
}

interface ProgressStep {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  status: 'pending' | 'active' | 'completed' | 'error';
  progress: number;
}

const RealProgress: FC<RealProgressProps> = ({
  isVisible,
  onComplete,
  currentStep = '',
  stepProgress = 0,
}) => {
  const { t } = useTranslation();
  const [overallProgress, setOverallProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  const [stepsState, setSteps] = useState<ProgressStep[]>([]);

  // Обновляем шаги при изменении языка
  useEffect(() => {
    setSteps([
      {
        id: 'validation',
        label: t('progress.validation.title'),
        description: t('progress.validation.description'),
        icon: <PendingIcon />,
        status: 'pending',
        progress: 0,
      },
      {
        id: 'music',
        label: t('progress.music.title'),
        description: t('progress.music.description'),
        icon: <MusicNoteIcon />,
        status: 'pending',
        progress: 0,
      },
      {
        id: 'images',
        label: t('progress.images.title'),
        description: t('progress.images.description'),
        icon: <ImageIcon />,
        status: 'pending',
        progress: 0,
      },
      {
        id: 'upload',
        label: t('progress.upload.title'),
        description: t('progress.upload.description'),
        icon: <UploadIcon />,
        status: 'pending',
        progress: 0,
      },
      {
        id: 'creation',
        label: t('progress.creation.title'),
        description: t('progress.creation.description'),
        icon: <CreateIcon />,
        status: 'pending',
        progress: 0,
      },
    ]);
  }, [t]);

  useEffect(() => {
    if (!isVisible) {
      setOverallProgress(0);
      setIsComplete(false);
      setSteps(prev => prev.map(step => ({ ...step, status: 'pending', progress: 0 })));
      return;
    }

    // Обновляем прогресс на основе текущего шага
    const updateStepProgress = () => {
      setSteps(prev => prev.map(step => {
        if (step.id === currentStep) {
          return { ...step, status: 'active', progress: stepProgress };
        } else if (getStepIndex(step.id) < getStepIndex(currentStep)) {
          return { ...step, status: 'completed', progress: 100 };
        } else if (getStepIndex(step.id) > getStepIndex(currentStep)) {
          return { ...step, status: 'pending', progress: 0 };
        }
        return step;
      }));
    };

    updateStepProgress();

    // Вычисляем общий прогресс
    const totalSteps = stepsState.length;
    const completedSteps = stepsState.filter(step => step.status === 'completed').length;
    const currentStepIndex = getStepIndex(currentStep);
    const overall = ((completedSteps + (stepProgress / 100)) / totalSteps) * 100;
    
    setOverallProgress(Math.min(overall, 100));

    // Если все шаги завершены, показываем завершение
    if (currentStep === 'complete') {
      setIsComplete(true);
      setTimeout(() => {
        onComplete?.();
      }, 2000);
    }
  }, [isVisible, currentStep, stepProgress, onComplete, t]);

  const getStepIndex = (stepId: string): number => {
    const stepOrder = ['validation', 'music', 'images', 'upload', 'creation', 'complete'];
    return stepOrder.indexOf(stepId);
  };

  const getStepIcon = (status: string, icon: React.ReactNode) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: '#4CAF50' }} />;
      case 'active':
        return icon;
      case 'error':
        return <ErrorIcon sx={{ color: '#F44336' }} />;
      default:
        return <PendingIcon sx={{ color: '#9E9E9E' }} />;
    }
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'completed':
        return '#4CAF50';
      case 'active':
        return '#2196F3';
      case 'error':
        return '#F44336';
      default:
        return '#9E9E9E';
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Fade in={isVisible} timeout={500}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'center',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingTop: { xs: 1.5, sm: 0 },
          paddingBottom: { xs: 'calc(env(safe-area-inset-bottom) + 8px)', sm: 0 },
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: { xs: 1.5, sm: 2.5, md: 4 },
            background: isComplete 
              ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            width: { xs: '92vw', sm: 'auto' },
            minWidth: { xs: 'auto', sm: '460px' },
            maxWidth: '90vw',
            maxHeight: { xs: 'calc(100dvh - 24px)', sm: '90vh' },
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            pb: { xs: 'calc(env(safe-area-inset-bottom) + 12px)', sm: 3, md: 4 },
            m: { xs: 1, sm: 0 },
            animation: 'slideIn 0.5s ease-out',
            '@keyframes slideIn': {
              '0%': {
                opacity: 0,
                transform: 'scale(0.9)',
              },
              '100%': {
                opacity: 1,
                transform: 'scale(1)',
              },
            },
          }}
        >
        <Stack spacing={2}>
          <Box textAlign="center">
            <Typography variant="h4" fontWeight="bold" mb={1} sx={{ fontSize: { xs: '1.1rem', sm: '1.35rem', md: '1.75rem' }, lineHeight: 1.2 }}>
              {isComplete ? t('progress.complete.title') : t('progress.title')}
            </Typography>
            {!isComplete && (
              <Typography variant="body1" sx={{ opacity: 0.9, fontSize: { xs: '0.85rem', sm: '0.95rem' }, lineHeight: 1.3, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                {t('progress.subtitle')}
              </Typography>
            )}
          </Box>
          
          {!isComplete && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ gap: { xs: 1, sm: 2 } }}>
                <Typography variant="h6" sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                  {t('progress.overall')}
                </Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                  {Math.round(overallProgress)}%
                </Typography>
              </Box>
              
              <LinearProgress
                variant="determinate"
                value={overallProgress}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#80FFB3',
                    borderRadius: 6,
                    transition: 'width 0.3s ease-in-out',
                  },
                }}
              />
            </Box>
          )}
          
          <Stack spacing={{ xs: 1, sm: 1.25, md: 1.5 }}>
            {stepsState.map((step) => {
              const isExpanded = step.status === 'active';
              return (
                <Box
                  key={step.id}
                  sx={{
                    p: { xs: isExpanded ? 1 : 0.75, sm: isExpanded ? 1.25 : 1, md: isExpanded ? 1.5 : 1.25 },
                    borderRadius: 2,
                    backgroundColor: isExpanded ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    border: isExpanded ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'auto 1fr auto', sm: 'auto 1fr auto' },
                      alignItems: 'center',
                      columnGap: { xs: 1, sm: 2 },
                      rowGap: { xs: 0.5, sm: 0.75 },
                      mb: isExpanded ? 1 : 0,
                    }}
                  >
                    <Box>{getStepIcon(step.status, step.icon)}</Box>
                    <Typography
                      variant="h6"
                      fontWeight="medium"
                      sx={{
                        fontSize: { xs: '0.92rem', sm: '1.02rem' },
                        minWidth: 0,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {step.label}
                    </Typography>
                    <Chip
                      label={step.status === 'completed' ? t('progress.status.completed') :
                             step.status === 'active' ? t('progress.status.active') :
                             t('progress.status.pending')}
                      size="small"
                      sx={{
                        backgroundColor: getStepColor(step.status),
                        color: 'white',
                        fontWeight: 'bold',
                        '& .MuiChip-label': { fontSize: { xs: '0.7rem', sm: '0.75rem' }, px: 1 },
                        justifySelf: 'end',
                      }}
                    />
                  </Box>

                  {isExpanded && (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.8,
                          ml: { xs: 3, sm: 4 },
                          fontSize: { xs: '0.78rem', sm: '0.88rem' },
                          lineHeight: 1.3,
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {step.description}
                      </Typography>

                      {step.progress > 0 && (
                        <Box mt={{ xs: 0.5, sm: 0.75 }} ml={{ xs: 3, sm: 4 }}>
                          <LinearProgress
                            variant="determinate"
                            value={step.progress}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: '#80FFB3',
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              );
            })}
          </Stack>
          
          {isComplete && (
            <Box textAlign="center" mt={2}>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                {t('progress.complete.message')}
              </Typography>
            </Box>
          )}
                  </Stack>
        </Paper>
      </Box>
    </Fade>
  );
};

export default RealProgress; 